import Swiper from "swiper"
import { EffectCoverflow, Zoom, Navigation } from "swiper/modules"
import "swiper/css"
import "swiper/css/effect-coverflow"
import "swiper/css/zoom"
import "swiper/css/navigation"
import { t } from "./i18n.js"

// Ties each tip bubble to its button with aria-controls; ids have to be unique
// across every carousel on the page, so the counter lives outside the class.
let zoomHintId = 0

/**
 * CarouselManager: Swiper-based carousel with coverflow depth, zoom, captions, and video autoplay.
 */
export class CarouselManager {
    constructor(root) {
        this.root = root
        this.swiper = null
        this.slides = Array.from(root.querySelectorAll(".swiper-slide"))
        this.volume = 1
        this.isMuted = false
        this.isScreenActive = false

        if (this.slides.length === 0) return

        this.init()
    }

    init() {
        // Detect if carousel contains video slides — use vertical mode
        this.isVertical = this.slides.some(s => s.querySelector("video"))

        // Create navigation arrows using scroll-down.svg rotated
        this.createNavArrows()
        this.setupPinchPrevention()

        // Hide nav arrows when only one slide
        if (this.slides.length === 1) {
            this.navPrev.style.display = "none"
            this.navNext.style.display = "none"
        }

        const baseConfig = {
            modules: [EffectCoverflow, Zoom, Navigation],
            navigation: {
                nextEl: this.navNext,
                prevEl: this.navPrev,
            },
            grabCursor: true,
            centeredSlides: true,
            slideToClickedSlide: true,
            speed: 500,
            zoom: {
                maxRatio: 4,
                minRatio: 1,
                toggle: true,
            },
            on: {
                slideChange: (swiper) => {
                    this.handleVideoPlayback(swiper.activeIndex)
                },
            },
        }

        if (this.isVertical) {
            // Vertical carousel for video slides
            this.swiper = new Swiper(this.root, {
                ...baseConfig,
                direction: "vertical",
                effect: "coverflow",
                slidesPerView: 1.5,
                coverflowEffect: {
                    rotate: 0,
                    stretch: 0,
                    depth: 0,
                    scale: 0.9,
                    modifier: 1,
                    slideShadows: false,
                },
                breakpoints: {
                    640: {
                        direction: "horizontal",
                        slidesPerView: 3,
                    },
                },
            })
            this.root.classList.add("carousel--vertical")
        } else {
            // Horizontal coverflow carousel for images
            this.swiper = new Swiper(this.root, {
                ...baseConfig,
                effect: "coverflow",
                slidesPerView: 1.2,
                coverflowEffect: {
                    rotate: 0,
                    stretch: 0,
                    depth: 0,
                    scale: 0.9,
                    modifier: 1,
                    slideShadows: false,
                },
                breakpoints: {
                    480: {
                        slidesPerView: "auto",
                    },
                },
            })
        }

        // Zoom hint button + tip on every zoom container.
        this.createZoomHints()

        // Create controls and playlist behavior for each video slide.
        this.createVideoControls()
        this.setupVideoEndListeners()
        this.applyAudioState()

        // Custom double-tap zoom for video slides because Swiper Zoom ignores videos
        if (this.isVertical) {
            this.setupVideoDoubleTapZoom()
        }

        // Listen for screen changes to autoplay/pause videos
        this.setupScreenChangeListeners()
    }

    // Zoom hint — a real button per zoom container, carrying a tip the visitor
    // can open. It deliberately does not zoom: double-tapping the media does,
    // and saying so is the button's entire job. A pseudo-element could not do
    // this at all, which is why the badge became an element.
    createZoomHints() {
        this.root.querySelectorAll(".swiper-zoom-container").forEach((container) => {
            const hint = document.createElement("div")
            hint.className = "zoom-hint"

            const icon = document.createElement("button")
            icon.type = "button"
            icon.className = "zoom-hint__icon"
            // The accessible name is the button's purpose, not the tip: the tip
            // is the thing it reveals, and aria-controls ties the two together.
            icon.setAttribute("aria-label", t("tip", "Tip"))
            icon.setAttribute("aria-expanded", "false")

            const bubble = document.createElement("div")
            bubble.className = "zoom-hint__bubble"
            bubble.id = `zoom-hint-${++zoomHintId}`
            bubble.setAttribute("role", "tooltip")
            // An editor's own phrasing beats the generic one: field_media_tips is
            // set per question and the template hangs it on the carousel, so it
            // covers that question's slides and nothing else. Read as text, not
            // markup -- the field is a plain string (field.storage.paragraph
            // .field_media_tips).
            const authored = (this.root.dataset.zoomTip ?? "").trim()
            bubble.textContent = authored || t("zoomTip", "Double-click to zoom in or out.")
            bubble.hidden = true
            icon.setAttribute("aria-controls", bubble.id)

            hint.append(icon, bubble)
            container.append(hint)
            this.bindZoomHint(hint, icon, bubble)
        })
    }

    // Hover reveals the tip, a click pins it, anything else closes it — the
    // behaviour the old per-question tip button had, now generic and on every
    // slide. Hover is not enough on its own: touch has none, and touch is where
    // the media is too small to read in the first place.
    bindZoomHint(hint, icon, bubble) {
        let pinned = false

        const show = () => {
            bubble.hidden = false
            hint.classList.add("is-open")
            icon.setAttribute("aria-expanded", "true")
        }
        const hide = () => {
            if (pinned) return
            bubble.hidden = true
            hint.classList.remove("is-open")
            icon.setAttribute("aria-expanded", "false")
        }
        const unpin = () => {
            pinned = false
            hide()
        }

        hint.addEventListener("mouseenter", show)
        hint.addEventListener("mouseleave", hide)
        icon.addEventListener("focus", show)
        icon.addEventListener("blur", unpin)

        // Only the click is stopped, never touchstart or pointerdown. quiz.js
        // measures its swipe from the touchstart it sees on document and reads
        // the distance again on touchend; swallowing the start leaves that
        // baseline at the previous swipe's position, so the tap that opens this
        // tip is measured against wherever the visitor last swiped from and the
        // quiz jumps a step. Letting the start through makes the delta the tap's
        // own ~0px, which quiz.js ignores.
        //
        // Swiper needs no guard either: the button only accepts a press on the
        // active slide, and slideToClickedSlide does nothing when the clicked
        // slide is already the active one.
        icon.addEventListener("click", (e) => {
            e.stopPropagation()
            e.preventDefault()
            if (pinned) {
                unpin()
            } else {
                pinned = true
                show()
            }
        })

        document.addEventListener("click", (e) => {
            if (!pinned || hint.contains(e.target)) return
            unpin()
        })
        document.addEventListener("keydown", (e) => {
            if (e.key === "Escape" && pinned) unpin()
        })
    }

    // Block secondary touch pointers before they reach Swiper's pinch handler.
    setupPinchPrevention() {
        const activePointers = new Set()
        const blockedPointers = new Set()

        // Prevent Swiper from receiving a second simultaneous touch pointer.
        const blockPointer = (e) => {
            e.preventDefault()
            e.stopImmediatePropagation()
        }
        this.root.addEventListener("pointerdown", (e) => {
            if (e.pointerType !== "touch") return
            if (activePointers.size > 0) {
                blockedPointers.add(e.pointerId)
                blockPointer(e)
            }
            activePointers.add(e.pointerId)
        }, { capture: true })
        this.root.addEventListener("pointermove", (e) => {
            if (blockedPointers.has(e.pointerId)) blockPointer(e)
        }, { capture: true })
        // Also suppress native multi-touch gestures on touch-event browsers.
        const blockMultiTouch = (e) => {
            if (e.touches.length > 1) e.preventDefault()
        }
        this.root.addEventListener("touchstart", blockMultiTouch, { capture: true, passive: false })
        this.root.addEventListener("touchmove", blockMultiTouch, { capture: true, passive: false })

        // Release pointer state even when the touch ends outside the carousel.
        const releasePointer = (e) => {
            activePointers.delete(e.pointerId)
            if (!blockedPointers.delete(e.pointerId)) return
            blockPointer(e)
        }
        this.root.addEventListener("pointerup", releasePointer, { capture: true })
        this.root.addEventListener("pointercancel", releasePointer, { capture: true })
        // Clear stale pointers that finish outside the carousel.
        const cleanupPointer = (e) => {
            activePointers.delete(e.pointerId)
            blockedPointers.delete(e.pointerId)
        }
        document.addEventListener("pointerup", cleanupPointer)
        document.addEventListener("pointercancel", cleanupPointer)
    }

    // Enable double-tap zoom and one-finger drag for video slides.
    setupVideoDoubleTapZoom() {
        const zoomContainers = this.root.querySelectorAll(".swiper-zoom-container")
        let lastTapTime = 0
        let isZoomed = false
        let currentScale = 1
        let translateX = 0
        let translateY = 0
        let startX = 0
        let startY = 0

        zoomContainers.forEach((container) => {
            const video = container.querySelector("video")
            if (!video) return

            // Swiper's Zoom module sets this class itself on image slides; this
            // hand-rolled video zoom has to, so the zoom hint badge (_carousel.scss)
            // disappears here for the same reason it does there.
            const slide = container.closest(".swiper-slide")

            // Double-tap to toggle zoom
            container.addEventListener("touchend", (e) => {
                if (e.touches.length > 0) return
                const now = Date.now()
                if (now - lastTapTime < 300) {
                    isZoomed = !isZoomed
                    currentScale = isZoomed ? 2.5 : 1
                    translateX = 0
                    translateY = 0
                    applyTransform(video, currentScale, translateX, translateY)
                    slide?.classList.toggle("swiper-slide-zoomed", isZoomed)
                }
                lastTapTime = now
            })

            container.addEventListener("touchstart", (e) => {
                if (e.touches.length !== 1 || currentScale <= 1) return
                startX = e.touches[0].clientX
                startY = e.touches[0].clientY
            }, { passive: true })

            container.addEventListener("touchmove", (e) => {
                if (e.touches.length !== 1 || currentScale <= 1) return
                e.stopPropagation()
                e.preventDefault()
                const dx = e.touches[0].clientX - startX
                const dy = e.touches[0].clientY - startY
                translateX += dx
                translateY += dy
                startX = e.touches[0].clientX
                startY = e.touches[0].clientY
                applyTransform(video, currentScale, translateX, translateY)
            }, { passive: false })

            container.addEventListener("touchend", (e) => {
                if (e.touches.length === 0 && currentScale <= 1.01) {
                    isZoomed = false
                    currentScale = 1
                    translateX = 0
                    translateY = 0
                    applyTransform(video, 1, 0, 0)
                    slide?.classList.remove("swiper-slide-zoomed")
                }
            }, { passive: true })
        })

        // Helper: apply CSS transform to video
        function applyTransform(el, scale, x, y) {
            el.style.transform = `scale(${scale}) translate(${x / scale}px, ${y / scale}px)`
            el.style.transformOrigin = "center center"
        }
    }

    // Check whether the carousel's nested sub-screen is currently visible
    isSubScreenActive() {
        const subScreen = this.root.closest(".screens")
        return !subScreen || subScreen.classList.contains("active")
    }

    // Play active video when its quiz screen and nested sub-screen are active
    setupScreenChangeListeners() {
        const screen = this.root.closest(".quiz__screen")
        if (!screen) return

        const updatePlayback = (isCurrentScreen, activeSubScreen = null) => {
            const isActiveSubScreen = activeSubScreen
                ? activeSubScreen.contains(this.root)
                : this.isSubScreenActive()
            this.isScreenActive = isCurrentScreen && isActiveSubScreen
            if (this.isScreenActive) {
                this.handleVideoPlayback(this.swiper?.activeIndex ?? 0)
            } else {
                this.pauseAllVideos()
            }
        }

        const screenRect = screen.getBoundingClientRect()
        updatePlayback(screenRect.top <= window.innerHeight / 2 && screenRect.bottom >= window.innerHeight / 2)

        document.addEventListener("quiz:screen-change", (e) => {
            updatePlayback(e.detail.screen === screen)
        })
        document.addEventListener("quiz:sub-screen-change", (e) => {
            if (e.detail.screen === screen) updatePlayback(true, e.detail.subScreen)
        })
    }

    // Pause all videos in carousel
    pauseAllVideos() {
        this.slides.forEach((slide) => {
            const video = slide.querySelector("video")
            if (video) video.pause()
        })
    }

    // Apply the selected volume and mute preference to every video in the carousel.
    applyAudioState() {
        this.slides.forEach((slide) => {
            const video = slide.querySelector("video")
            if (!video) return

            video.volume = this.volume
            video.muted = this.isMuted
        })
    }

    // Play with sound first and fall back to muted playback when autoplay is rejected.
    async playVideo(video) {
        video.volume = this.volume
        video.muted = this.isMuted

        try {
            await video.play()
        } catch {
            video.muted = true
            try {
                await video.play()
            } catch {
                video.pause()
            }
        }
    }

    // Advance to the next video slide when the active video reaches its end.
    setupVideoEndListeners() {
        this.slides.forEach((slide, index) => {
            const video = slide.querySelector("video")
            if (!video) return

            video.addEventListener("ended", () => {
                if (!this.isScreenActive || this.swiper?.activeIndex !== index) return

                const nextVideoIndex = this.slides.findIndex(
                    (nextSlide, nextIndex) => nextIndex > index && nextSlide.querySelector("video")
                )
                if (nextVideoIndex !== -1) {
                    this.swiper.slideTo(nextVideoIndex)
                }
            })
        })
    }

    // Create custom playback, mute, volume, and fullscreen controls for each video slide.
    createVideoControls() {
        this.slides.forEach((slide) => {
            const video = slide.querySelector("video")
            if (!video) return

            const controls = document.createElement("div")
            controls.className = "video-controls"
            controls.addEventListener("pointerdown", (e) => e.stopPropagation())
            controls.addEventListener("click", (e) => e.stopPropagation())

            const playPause = document.createElement("button")
            playPause.className = "video-controls__btn video-controls__playpause"
            playPause.setAttribute("aria-label", t("playPauseVideo", "Play or pause video"))
            playPause.innerHTML = '<svg viewBox="0 0 24 24" width="24" height="24"><path class="icon-play" d="M8 5v14l11-7z" fill="currentColor"/><path class="icon-pause" d="M6 5h4v14H6zM14 5h4v14h-4z" fill="currentColor" style="display:none"/></svg>'
            playPause.addEventListener("click", () => {
                if (video.paused) {
                    this.playVideo(video)
                } else {
                    video.pause()
                }
            })
            video.addEventListener("play", () => {
                playPause.querySelector(".icon-play").style.display = "none"
                playPause.querySelector(".icon-pause").style.display = ""
            })
            video.addEventListener("pause", () => {
                playPause.querySelector(".icon-play").style.display = ""
                playPause.querySelector(".icon-pause").style.display = "none"
            })

            const mute = document.createElement("button")
            mute.className = "video-controls__btn video-controls__mute"
            mute.innerHTML = '<svg viewBox="0 0 24 24" width="24" height="24"><path class="icon-muted" d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z" fill="currentColor"/><path class="icon-unmuted" d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" fill="currentColor"/></svg>'

            const volume = document.createElement("input")
            volume.type = "range"
            volume.className = "video-controls__volume"
            volume.min = "0"
            volume.max = "1"
            volume.step = "0.05"
            volume.value = String(this.volume)
            volume.setAttribute("aria-label", t("videoVolume", "Video volume"))

            const updateAudioControls = () => {
                const isSilent = video.muted || video.volume === 0
                mute.setAttribute("aria-label", isSilent ? t("unmuteVideo", "Unmute video") : t("muteVideo", "Mute video"))
                mute.querySelector(".icon-muted").style.display = isSilent ? "" : "none"
                mute.querySelector(".icon-unmuted").style.display = isSilent ? "none" : ""
                volume.value = String(video.volume)
            }
            mute.addEventListener("click", () => {
                this.isMuted = !(video.muted || video.volume === 0)
                if (!this.isMuted && this.volume === 0) this.volume = 1
                this.applyAudioState()
            })
            volume.addEventListener("input", () => {
                this.volume = Number(volume.value)
                this.isMuted = this.volume === 0
                this.applyAudioState()
            })
            video.addEventListener("volumechange", updateAudioControls)
            updateAudioControls()

            const fullscreen = document.createElement("button")
            fullscreen.className = "video-controls__btn video-controls__fullscreen"
            fullscreen.setAttribute("aria-label", t("fullscreen", "Fullscreen"))
            fullscreen.innerHTML = '<svg viewBox="0 0 24 24" width="24" height="24"><path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" fill="currentColor"/></svg>'
            fullscreen.addEventListener("click", () => {
                if (document.fullscreenElement) {
                    document.exitFullscreen()
                } else if (video.requestFullscreen) {
                    video.requestFullscreen()
                } else if (video.webkitRequestFullscreen) {
                    video.webkitRequestFullscreen()
                }
            })

            const updateFullscreenStyles = () => {
                const isFullscreen = document.fullscreenElement === video || document.webkitFullscreenElement === video
                video.style.objectFit = isFullscreen ? "contain" : ""
                video.style.width = isFullscreen ? "100%" : ""
                video.style.height = isFullscreen ? "100%" : ""
            }
            document.addEventListener("fullscreenchange", updateFullscreenStyles)
            document.addEventListener("webkitfullscreenchange", updateFullscreenStyles)

            controls.appendChild(playPause)
            controls.appendChild(mute)
            controls.appendChild(volume)
            controls.appendChild(fullscreen)
            slide.appendChild(controls)
        })
    }

    // Create prev/next arrow buttons using scroll-down.svg rotated 90deg
    createNavArrows() {
        const arrowUrl = new URL("../images/scroll-down.svg", import.meta.url).href

        this.navPrev = document.createElement("button")
        this.navPrev.className = "carousel__nav carousel__nav--prev"
        this.navPrev.setAttribute("aria-label", t("previous", "Previous"))
        this.navPrev.innerHTML = `<img src="${arrowUrl}" alt="" />`

        this.navNext = document.createElement("button")
        this.navNext.className = "carousel__nav carousel__nav--next"
        this.navNext.setAttribute("aria-label", t("next", "Next"))
        this.navNext.innerHTML = `<img src="${arrowUrl}" alt="" />`

        this.root.parentElement.appendChild(this.navPrev)
        this.root.parentElement.appendChild(this.navNext)
    }

    // Play the active slide's video only while its quiz screen is visible.
    handleVideoPlayback(activeIndex) {
        this.slides.forEach((slide, index) => {
            const video = slide.querySelector("[data-autoplay-on-view]")
            if (!video) return

            if (this.isScreenActive && index === activeIndex) {
                if (video.ended) video.currentTime = 0
                this.playVideo(video)
            } else {
                video.pause()
            }
        })
    }
}

// Initialize all carousels on the page
export function initCarousels() {
    const carousels = document.querySelectorAll("[data-carousel]")
    const instances = []
    carousels.forEach((el) => {
        instances.push(new CarouselManager(el))
    })
    return instances
}
